import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { api, DONATE_URL } from "@/lib/api";
import { FadeUp } from "@/components/Reveal";

export default function ArticlePage() {
  const { id } = useParams();
  const [article, setArticle] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [correspondents, setCorrespondents] = useState([]);

  useEffect(() => {
    api.get(`/articles/${id}`)
      .then(({ data }) => setArticle(data))
      .catch(() => setNotFound(true));
    api.get("/correspondents").then(({ data }) => setCorrespondents(data)).catch(() => {});
  }, [id]);

  if (notFound)
    return (
      <div data-testid="article-notfound" className="min-h-screen grid place-items-center px-6 text-center">
        <div>
          <p className="font-display text-4xl font-bold mb-4">Story not found</p>
          <Link to="/" className="label link-underline pb-1">Back home</Link>
        </div>
      </div>
    );

  if (!article)
    return <div className="min-h-screen grid place-items-center font-sans text-ink/50">Loading…</div>;

  return (
    <article data-testid="article-page" className="pt-[92px]">
      {/* Hero */}
      <header className="max-w-4xl mx-auto px-5 sm:px-8 pt-16 pb-10">
        <Link to="/" data-testid="article-back-link" className="inline-flex items-center gap-2 label text-ink/50 hover:text-ink mb-8">
          <ArrowLeft size={15} /> All stories
        </Link>
        <FadeUp>
          <p className="label text-ink/50 mb-5">{article.category} · {article.read_time} min read</p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black leading-[0.98] tracking-tight">
            {article.title}
          </h1>
          <p className="mt-6 text-lg text-ink/70 leading-relaxed">{article.excerpt}</p>
          <p className="mt-6 label text-ink/60">
            By{" "}
            {(() => {
              const match = correspondents.find(
                (c) => c.name.trim().toLowerCase() === (article.author || "").trim().toLowerCase()
              );
              return match ? (
                <Link to={`/correspondent/${match.id}`} data-testid="article-author-link" className="text-ink hover:text-canary link-underline pb-0.5">
                  {article.author}
                </Link>
              ) : (
                <span>{article.author}</span>
              );
            })()}
          </p>
        </FadeUp>
      </header>

      <div className="hover-img max-w-6xl mx-auto px-5 sm:px-8">
        <div className="aspect-[16/9] bg-paper">
          <img src={article.cover_image} alt={article.title} className="h-full w-full object-cover" />
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-5 sm:px-8 py-14">
        {article.body.split("\n").filter(Boolean).map((p, i) => (
          <p key={i} className="text-lg sm:text-xl text-ink/80 leading-[1.8] mb-7 first:first-letter:font-display first:first-letter:text-6xl first:first-letter:font-black first:first-letter:float-left first:first-letter:mr-3 first:first-letter:leading-[0.8]">
            {p}
          </p>
        ))}

        <div className="mt-12 border-t border-ink/15 pt-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <p className="font-display text-2xl font-bold max-w-sm leading-tight">Support reporting like this.</p>
          <a
            href={DONATE_URL} target="_blank" rel="noopener noreferrer"
            data-testid="article-donate-btn"
            className="inline-flex items-center gap-2 label bg-ink text-white px-8 py-4 rounded-full hover:bg-canary hover:text-ink transition-colors duration-300"
          >
            Donate <ArrowUpRight size={15} />
          </a>
        </div>
      </div>
    </article>
  );
}
